import React, { memo } from 'react';
import { Handle, Position } from '@xyflow/react';
import { NODE_DEFS, ACCENT_COLORS } from '../../data/nodeDefinitions';
import Icon from '../ui/Icon';
import styles from './JourneyNode.module.css';

const ENTRY_MODE_LABEL = { segment: 'Segment', event: 'Event', schedule: 'Scheduled', attribute: 'Attribute', manual: 'Manual list' };

function nodeBodyText(type, cfg, segmentNames) {
  if (!cfg) return '';
  // Prefer the live segment name (resolved by id) over any cached name, falling
  // back to the id only if the segment can't be found.
  const segLabel = (id, cached) => segmentNames?.[id] || cached || id || 'pick audience';
  switch (type) {
    case 'entry': {
      const mode = cfg.mode || 'segment';
      const tag = ENTRY_MODE_LABEL[mode] || mode;
      let detail;
      if (mode === 'event') detail = cfg.event || 'event';
      else if (mode === 'schedule') detail = (cfg.cron || 'cron') + (cfg.segmentId ? ' · ' + segLabel(cfg.segmentId, cfg.segmentName) : '');
      else if (mode === 'attribute') detail = cfg.attribute || 'attribute';
      else if (mode === 'manual') detail = 'user list';
      else detail = segLabel(cfg.segmentId, cfg.segmentName);
      return tag + ' · ' + detail;
    }
    case 'entry-event':     return cfg.event || '—';
    case 'entry-schedule':  return cfg.preset || cfg.cron || 'Custom cron';
    case 'entry-segment':   return segLabel(cfg.segmentId, cfg.segmentName);
    case 'entry-api':       return cfg.description || 'API Trigger';
    case 'action-push':     return cfg.title || 'No title set';
    case 'action-whatsapp': return cfg.isCustomText ? (cfg.text ? cfg.text.slice(0, 30) + '…' : 'No message') : (cfg.templateName || 'No template');
    case 'action-email':    return cfg.subject || 'No subject';
    case 'action-sms':      return cfg.text ? cfg.text.slice(0, 30) + '…' : 'No message';
    case 'action-rcs':      return cfg.templateId || 'No template';
    case 'action-webhook':  return cfg.url || 'No URL';
    case 'wait-relative':   return 'Wait ' + (cfg.amount || '1') + ' ' + (cfg.unit || 'hours');
    case 'wait-absolute':   return cfg.rule === 'next_day_noon' ? 'Tomorrow 12 PM' : (cfg.rule || '—');
    case 'logic-condition': {
      const labels = { if_not_opened: "Hasn't opened previous step", if_delivered: 'Previous step delivered', if_failed: 'Previous step failed' };
      if (cfg.condType === 'event_performed') return 'Did: ' + (cfg.event || 'event');
      if (cfg.condType === 'event_not_performed') return "Didn't: " + (cfg.event || 'event');
      if (cfg.condType === 'user_property') return (cfg.attrField || 'attribute') + ' ' + (cfg.attrOperator || '=') + ' ' + (cfg.attrValue || '?');
      return labels[cfg.condType] || cfg.condType || '—';
    }
    case 'logic-ab':        return (cfg.splitA || '50') + '% / ' + (cfg.splitB || '50') + '%';
    case 'logic-goal':      return cfg.event || '—';
    case 'logic-exit':      return 'End of journey';
    default:                return '';
  }
}

// Live View metric strip rendered under a node's body. Values sourced from
// transitions show "—" when transition data isn't available yet (honest, not a
// fake 0); the message funnel is shown independently.
function NodeMetrics({ m }) {
  if (!m) return null;
  const t = (v) => (m.tAvail && v != null ? String(v) : '—');
  const wrap = { borderTop: '1px dashed var(--b1)', padding: '5px 8px', display: 'flex', flexDirection: 'column', gap: 3, fontSize: 9, fontVariantNumeric: 'tabular-nums' };
  const row = { display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' };
  const lbl = { color: 'var(--t3)' };
  const stat = (label, val, color) => (
    <span key={label} style={lbl}>{label} <b style={{ color: color || 'var(--t1)', fontWeight: 700 }}>{val}</b></span>
  );

  if (m.kind === 'entry') {
    return <div style={wrap}><div style={row}>{stat('Admitted', t(m.admitted))}{stat('Active', m.active, 'var(--blueHi)')}{stat('Done', m.completed, 'var(--greenHi)')}</div></div>;
  }
  if (m.kind === 'exit') {
    return <div style={wrap}><div style={row}>{stat('Completed', m.completed, 'var(--greenHi)')}</div></div>;
  }
  if (m.kind === 'branch') {
    const b = m.branches || {};
    const parts = Object.keys(b).length ? Object.entries(b).map(([k, v]) => stat(k.toUpperCase(), m.tAvail ? v : '—', 'var(--purpleHi)')) : [<span key="na" style={lbl}>no branch data</span>];
    return <div style={wrap}><div style={row}>{stat('In', t(m.entered))}</div><div style={row}>{parts}</div></div>;
  }
  if (m.kind === 'step') {
    return <div style={wrap}><div style={row}>{stat('In', t(m.entered))}{stat('Waiting', t(m.waiting), 'var(--amberHi)')}</div></div>;
  }
  // send
  return (
    <div style={wrap}>
      <div style={row}>{stat('In', t(m.entered))}{stat('Waiting', t(m.waiting), 'var(--amberHi)')}</div>
      <div style={row}>
        {stat('Sent', m.sent)}
        {stat('Del', m.delivered, 'var(--greenHi)')}
        {stat('Open', m.opened, 'var(--blueHi)')}
        {stat('Clk', m.clicked, 'var(--purpleHi)')}
        {stat('Fail', m.failed, 'var(--redHi)')}
      </div>
    </div>
  );
}

// Named source handles are spread evenly along the bottom edge so branch
// labels (Yes/No, A/B) line up visually with their own connector.
function outHandleStyle(index, total) {
  if (total <= 1) return undefined;
  const pct = ((index + 1) / (total + 1)) * 100;
  return { left: pct + '%' };
}

function JourneyNode({ id, data, selected }) {
  const { nodeType, cfg, onDelete, metrics, segmentNames } = data;
  const def = NODE_DEFS[nodeType] || {};
  const accentColor = ACCENT_COLORS[def.accentType] || '#888';
  const body = nodeBodyText(nodeType, cfg, segmentNames);
  const handles = def.handles || { in: true, out: ['out'] };

  return (
    <div
      className={[styles.node, selected ? styles.selected : '', def.comingSoon ? styles.comingSoon : ''].join(' ')}
      style={{ '--accent': accentColor }}
    >
      {handles.in && <Handle type="target" position={Position.Top} className={styles.handle} />}

      <div className={styles.header} style={{ borderBottom: '1px solid ' + accentColor + '30' }}>
        <div className={styles.ico} style={{ background: def.icoC, color: def.icoT }}>
          <Icon name={def.ico} size={16} />
        </div>
        <div className={styles.label}>{def.label}</div>
        {def.comingSoon && <span className={styles.soonBadge} title="The journey engine doesn't run this step type yet — safe to plan with, won't execute on publish.">Soon</span>}
        <button
          className={styles.del}
          onClick={e => { e.stopPropagation(); onDelete && onDelete(id); }}
        ><span className="material-symbols-rounded" style={{ fontSize: 13 }}>close</span></button>
      </div>

      {body && (
        <div className={styles.body}>
          <div className={styles.bodyText}>{body}</div>
        </div>
      )}

      {cfg?.repeatEnabled && (
        <div style={{ padding: '3px 8px', fontSize: 10, color: 'var(--amberHi)', display: 'flex', alignItems: 'center', gap: 4 }}>
          <span className="material-symbols-rounded" style={{ fontSize: 12 }}>repeat</span>
          Repeats every {cfg.repeatEvery || 'day'} @ {cfg.repeatAt || '09:00'}
        </div>
      )}

      {metrics && <NodeMetrics m={metrics} />}

      {def.hasBranches && def.branches && (
        <div className={styles.branches}>
          {def.branches.map((b, i) => (
            <div key={i} className={[styles.branch, styles['branch-' + def.branchStyles[i]]].join(' ')}>
              {b}
            </div>
          ))}
        </div>
      )}

      {handles.out.map((handleId, i) => (
        <Handle
          key={handleId}
          id={handleId}
          type="source"
          position={Position.Bottom}
          className={styles.handle}
          style={outHandleStyle(i, handles.out.length)}
        />
      ))}
    </div>
  );
}

export default memo(JourneyNode);
